import { useState, useEffect } from 'react';
import { cachedFetchJSON } from '../utils/fetchCache';

// CDC Places county endpoint (all geographies)
const CDC_BASE = 'https://data.cdc.gov/resource';

// County-level PLACES dataset (used for county aggregates + state comparisons)
// https://data.cdc.gov/resource/swc5-untb.json
const CDC_COUNTY_VIEW_ID = 'swc5-untb';

// Census tract-level PLACES dataset — available for urban/large counties
// Same measure IDs as the county dataset; finer resolution when available
// https://data.cdc.gov/resource/cwsq-ngmh.json
const CDC_TRACT_VIEW_ID = 'cwsq-ngmh';

function buildTrend(rows, allowedYears) {
  const years = [...new Set(rows.map(r => parseInt(r.year, 10)).filter(Boolean))];
  years.sort((a, b) => b - a);
  return years
    .filter(y => allowedYears ? allowedYears.includes(y) : true)
    .map(year => {
      const vals = rows
        .filter(r => parseInt(r.year, 10) === year)
        .map(r => parseFloat(r.data_value))
        .filter(v => !isNaN(v));
      if (!vals.length) return null;
      return { year, value: vals.reduce((s, v) => s + v, 0) / vals.length };
    })
    .filter(Boolean);
}

/**
 * Hook: fetches all CDC Places indicators from config.
 *
 * For each indicator, probes the census tract dataset first (finer resolution,
 * available for urban/large counties). Falls back to county-level if no tract
 * data exists for the home county.
 *
 * geographyOverride modes:
 *   'group'  — uses county-level state data, filters to group counties, averages
 *   'tracts' — CDC is inherently county-level; uses the county from the override
 *   default  — probe tract dataset first, fall back to county dataset
 *
 * Returns:
 *   data: Map<indicatorId, {
 *     indicatorId, label, unit,
 *     granularity,  — 'tract' | 'county' — which resolution was used
 *     geography,    — [{ geoid, name, value }] latest year (11-digit if tract, 5-digit if county)
 *     trend,        — [{ year, value }] county aggregate, descending year
 *     stateTrend,   — [{ year, value }] state average, descending year
 *     countyValue,  — latest year county/group value
 *     stateValue    — latest year state average
 *   }>
 *   loading: boolean
 *   error: string | null
 */
export function useCDCData(config, overrideIndicators, geographyOverride) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indicators = (overrideIndicators ?? config.indicators).filter(i => i.source === 'cdc');
    if (indicators.length === 0) {
      setData(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const results = new Map();
        const stateFips = config.org.geography.state;
        const countyFips = `${stateFips}${config.org.geography.county}`; // 5-digit

        const nextStateFips = String(parseInt(stateFips, 10) + 1).padStart(2, '0');

        await Promise.all(indicators.map(async (indicator) => {
          if (!indicator.cdcViewId) {
            throw new Error(`Indicator "${indicator.id}" is missing required cdcViewId`);
          }

          // Build manually — URLSearchParams encodes '$' to '%24' which Socrata rejects
          const measureFilter = indicator.cdcMeasureId
            ? ` AND measureid='${indicator.cdcMeasureId}'`
            : '';

          // ── Mode 2: county group ─────────────────────────────────────────────
          if (geographyOverride?.mode === 'group') {
            const { state: gState, counties: groupCounties } = geographyOverride;
            const nextGState = String(parseInt(gState, 10) + 1).padStart(2, '0');

            const stateUrl =
              `${CDC_BASE}/${CDC_COUNTY_VIEW_ID}.json` +
              `?$where=locationid>='${gState}000' AND locationid<'${nextGState}000'${measureFilter}` +
              `&$select=locationid,locationname,data_value,year` +
              `&$limit=5000`;

            const statePayload = await cachedFetchJSON(stateUrl).catch(() => []);
            const stateRows = Array.isArray(statePayload) ? statePayload : [];

            const groupFips5 = new Set(groupCounties.map(c => `${gState}${c.fips}`));
            const groupRows = stateRows.filter(r => groupFips5.has(r.locationid));

            const groupYears = [...new Set(groupRows.map(r => parseInt(r.year, 10)).filter(Boolean))];
            groupYears.sort((a, b) => b - a);
            const latestGroupYear = groupYears[0];

            const geoByCo = new Map();
            if (latestGroupYear) {
              groupRows
                .filter(r => parseInt(r.year, 10) === latestGroupYear)
                .forEach(r => {
                  const val = parseFloat(r.data_value);
                  if (!isNaN(val)) {
                    const prev = geoByCo.get(r.locationid);
                    if (prev) {
                      prev.sum += val; prev.count++;
                      prev.value = prev.sum / prev.count;
                    } else {
                      geoByCo.set(r.locationid, { geoid: r.locationid, name: r.locationname, value: val, sum: val, count: 1 });
                    }
                  }
                });
            }
            const geography = [...geoByCo.values()].map(({ sum, count, ...rest }) => rest); // eslint-disable-line no-unused-vars
            const trend = buildTrend(groupRows, indicator.years ?? null);
            const countyValue = geography.length
              ? geography.reduce((s, g) => s + g.value, 0) / geography.length
              : null;

            results.set(indicator.id, {
              indicatorId: indicator.id, label: indicator.label, unit: indicator.unit,
              granularity: 'county',
              geography, stateGeography: [],
              trend, stateTrend: [],
              countyValue, stateValue: null
            });
            return;
          }

          // ── Default (Mode 1 / Mode 3) ─────────────────────────────────────────
          // Build URLs for both tract-level and county/state-level fetches
          const tractUrl =
            `${CDC_BASE}/${CDC_TRACT_VIEW_ID}.json` +
            `?$where=countyfips='${countyFips}'${measureFilter}` +
            `&$select=locationid,locationname,data_value,year` +
            `&$limit=5000`;

          const countyUrl =
            `${CDC_BASE}/${CDC_COUNTY_VIEW_ID}.json` +
            `?$where=locationid='${countyFips}'${measureFilter}` +
            `&$select=locationid,locationname,data_value,year` +
            `&$limit=100`;

          const stateUrl =
            `${CDC_BASE}/${CDC_COUNTY_VIEW_ID}.json` +
            `?$where=locationid>='${stateFips}000' AND locationid<'${nextStateFips}000'${measureFilter}` +
            `&$select=locationid,locationname,data_value,year` +
            `&$limit=5000`;

          // Probe tract dataset first — may be empty for rural counties
          const [tractPayload, statePayload] = await Promise.all([
            cachedFetchJSON(tractUrl).catch(() => []),
            cachedFetchJSON(stateUrl).catch(() => [])
          ]);

          const tractRows = Array.isArray(tractPayload) ? tractPayload : [];
          const stateRows = Array.isArray(statePayload) ? statePayload : [];

          // Build state geography and trend from county-level state data (used for state toggle)
          const stateTrend = buildTrend(stateRows, indicator.years ?? null);
          const stateValue = stateTrend.length > 0 ? stateTrend[0].value : null;
          const stateYears = [...new Set(stateRows.map(r => parseInt(r.year, 10)).filter(Boolean))];
          stateYears.sort((a, b) => b - a);
          const latestStateYear = stateYears[0];
          const stateGeography = latestStateYear
            ? stateRows
                .filter(r => parseInt(r.year, 10) === latestStateYear)
                .map(r => ({
                  geoid: r.locationid,
                  name: r.locationname,
                  value: r.data_value != null ? parseFloat(r.data_value) : null
                }))
                .filter(r => r.value !== null)
            : [];

          // ── Tract-level path (finer resolution available) ──────────────────
          if (tractRows.length > 0) {
            const tractYears = [...new Set(tractRows.map(r => parseInt(r.year, 10)).filter(Boolean))];
            tractYears.sort((a, b) => b - a);
            const latestTractYear = tractYears[0];

            // Deduplicate by locationid at latest year (take first row if any dupes)
            const geoByTract = new Map();
            tractRows
              .filter(r => parseInt(r.year, 10) === latestTractYear)
              .forEach(r => {
                if (!geoByTract.has(r.locationid)) {
                  const val = parseFloat(r.data_value);
                  if (!isNaN(val)) {
                    geoByTract.set(r.locationid, {
                      geoid: r.locationid, // 11-digit tract GEOID
                      name: r.locationname,
                      value: val
                    });
                  }
                }
              });

            const geography = [...geoByTract.values()];

            // Trend and countyValue = average of all tracts in home county per year
            const trend = buildTrend(tractRows, indicator.years ?? null);
            const countyValue = trend.length > 0 ? trend[0].value : null;

            results.set(indicator.id, {
              indicatorId: indicator.id, label: indicator.label, unit: indicator.unit,
              granularity: 'tract',
              geography,
              stateGeography,
              trend,
              stateTrend,
              countyValue,
              stateValue
            });
            return;
          }

          // ── County-level fallback ─────────────────────────────────────────
          const countyPayload = await cachedFetchJSON(countyUrl).catch(() => []);
          const rows = Array.isArray(countyPayload) ? countyPayload : [];

          const countyYears = [...new Set(rows.map(r => parseInt(r.year, 10)).filter(Boolean))];
          countyYears.sort((a, b) => b - a);
          const latestYear = countyYears[0];

          const geography = rows
            .filter(r => parseInt(r.year, 10) === latestYear)
            .map(r => ({
              geoid: r.locationid, // 5-digit county GEOID
              name: r.locationname,
              value: r.data_value != null ? parseFloat(r.data_value) : null
            }))
            .filter(r => r.value !== null);

          const trend = buildTrend(rows, indicator.years ?? null);
          const countyValue = trend.length > 0 ? trend[0].value : null;

          results.set(indicator.id, {
            indicatorId: indicator.id, label: indicator.label, unit: indicator.unit,
            granularity: 'county',
            geography,
            stateGeography,
            trend,
            stateTrend,
            countyValue,
            stateValue
          });
        }));

        if (!cancelled) {
          setData(results);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`CDC data failed to load: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, overrideIndicators, geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
